import type { DimensionsMm } from '../domain/schema';

export interface AirlineRuleCandidate {
  id: string;
  carrierCode: string;
  carrierName: string;
  cabinAllowanceG: number;
  mainBag?: DimensionsMm;
  personalItem?: DimensionsMm;
  mainBagBoundary?: 'inclusive' | 'exclusive';
  includedMainBags: number;
  includedPersonalItems: number;
  summary: string;
  exceptionNote: string;
  sourceUrl: string;
  sourceCheckedAt: string;
}

/**
 * Official-source candidates, not compliance decisions. The traveler must
 * confirm operating carrier, fare family, itinerary and current source.
 */
export const AIRLINE_RULE_CANDIDATES: AirlineRuleCandidate[] = [
  {
    id: 'KE-ECONOMY-20260809', carrierCode: 'KE', carrierName: '대한항공', cabinAllowanceG: 10_000,
    mainBag: { width: 400, height: 550, depth: 200 }, personalItem: { width: 300, height: 400, depth: 150 },
    includedMainBags: 1, includedPersonalItems: 1,
    summary: '일반석: 휴대 수하물 1개와 개인 소지품 1개, 합산 10kg 후보',
    exceptionNote: '공동운항, 공항·국가, 좌석 등급과 현장 상황에 따라 달라질 수 있어요.',
    sourceUrl: 'https://www.koreanair.com/contents/plan-your-travel/baggage/carry-on-baggage?hl=ko', sourceCheckedAt: '2026-10-18',
  },
  {
    id: 'OZ-ECONOMY-20260809', carrierCode: 'OZ', carrierName: '아시아나항공', cabinAllowanceG: 10_000,
    mainBag: { width: 400, height: 550, depth: 200 }, personalItem: { width: 300, height: 400, depth: 200 },
    includedMainBags: 1, includedPersonalItems: 1,
    summary: '이코노미: 휴대 수하물 1개 10kg과 개인 소지품 1개 후보',
    exceptionNote: '공동운항과 연결 구간은 실제 운항 항공사 규정이 다를 수 있어요.',
    sourceUrl: 'https://flyasiana.com/C/KR/KO/contents/carry-on-baggage', sourceCheckedAt: '2026-10-18',
  },
  {
    id: '7C-STANDARD-20260809', carrierCode: '7C', carrierName: '제주항공', cabinAllowanceG: 10_000,
    mainBag: { width: 400, height: 550, depth: 200 }, personalItem: { width: 300, height: 400, depth: 150 },
    includedMainBags: 1, includedPersonalItems: 1,
    summary: '기내 가방과 개인 소지품 합산 10kg 후보',
    exceptionNote: '운임, 노선, 공동운항과 구매한 부가서비스를 예약 상세에서 확인하세요.',
    sourceUrl: 'https://www.jejuair.net/ko/linkService/boardingProcessGuide/cabinBaggage.do', sourceCheckedAt: '2026-10-18',
  },
  {
    id: 'TW-STANDARD-20260809', carrierCode: 'TW', carrierName: '티웨이항공', cabinAllowanceG: 10_000,
    mainBag: { width: 400, height: 550, depth: 200 }, personalItem: { width: 300, height: 400, depth: 150 },
    includedMainBags: 1, includedPersonalItems: 1,
    summary: '일반 운임: 휴대 수하물과 개인 소지품 합산 10kg 후보',
    exceptionNote: '비즈니스 운임과 공동운항은 개수·무게가 달라질 수 있어요.',
    sourceUrl: 'https://www.twayair.com/app/serviceInfo/contents/1148', sourceCheckedAt: '2026-10-18',
  },
  {
    id: 'EK-ECONOMY-20260809', carrierCode: 'EK', carrierName: '에미레이트항공', cabinAllowanceG: 7_000,
    mainBag: { width: 380, height: 550, depth: 220 }, includedMainBags: 1, includedPersonalItems: 0,
    summary: '이코노미: 휴대 수하물 1개 7kg 후보',
    exceptionNote: '좌석 등급과 인도·브라질 등 일부 출발지 예외를 확인하세요.',
    sourceUrl: 'https://www.emirates.com/english/before-you-fly/baggage/cabin-baggage-rules/', sourceCheckedAt: '2026-10-18',
  },
  {
    id: 'AK-STANDARD-20260809', carrierCode: 'AK', carrierName: '에어아시아', cabinAllowanceG: 7_000,
    mainBag: { width: 360, height: 560, depth: 230 }, personalItem: { width: 300, height: 400, depth: 100 },
    includedMainBags: 1, includedPersonalItems: 1,
    summary: '기내 가방 1개와 소형 가방 1개, 합산 7kg 후보',
    exceptionNote: '실제 운항사와 예약 운임의 최신 안내를 다시 확인하세요.',
    sourceUrl: 'https://www.airasia.com/aa/about-us/en/gb/terms-and-conditions-ak.html?cur=USD', sourceCheckedAt: '2026-10-18',
  },
  {
    id: 'FR-BASIC-20260809', carrierCode: 'FR', carrierName: '라이언에어', cabinAllowanceG: 10_000,
    mainBag: { width: 400, height: 550, depth: 200 }, personalItem: { width: 300, height: 400, depth: 200 },
    includedMainBags: 0, includedPersonalItems: 1,
    summary: '기본 운임은 소형 개인 가방만, 우선 탑승 상품(Priority) 선택 시 10kg 가방 추가 후보',
    exceptionNote: '10kg 가방은 기본 포함이 아니므로 예약의 우선 탑승 상품(Priority)과 부가 수하물을 확인하세요.',
    sourceUrl: 'https://help.ryanair.com/hc/en-gb/categories/12489112419089-Bag-Rules', sourceCheckedAt: '2026-10-18',
  },
  { id: 'LJ-CANDIDATE-20261018', mainBagBoundary: 'exclusive', carrierCode: 'LJ', carrierName: '진에어', cabinAllowanceG: 10_000,
    mainBag: { width: 400, height: 550, depth: 200 },
    includedMainBags: 1, includedPersonalItems: 1, summary: '휴대 가방 10kg 후보 · 실제 운항편 확인 필요',
    exceptionNote: '공식 안내의 각 변 제한은 미만 기준이에요. 공항·운항편의 최신 안내를 다시 확인하세요.', sourceUrl: 'https://agent.jinair.com/announce/getDetail?seq=592', sourceCheckedAt: '2026-10-18' },
  { id: 'BX-CANDIDATE-20261018', carrierCode: 'BX', carrierName: '에어부산', cabinAllowanceG: 10_000,
    mainBag: { width: 400, height: 550, depth: 200 },
    includedMainBags: 1, includedPersonalItems: 1, summary: '휴대 가방 10kg 후보 · 실제 운항편 확인 필요',
    exceptionNote: '추가 개인 물품의 크기와 국제선 탑승구 위탁 규정을 확인하세요.', sourceUrl: 'https://www.airbusan.com/content/common/service/baggage/free', sourceCheckedAt: '2026-10-18' },
  { id: 'RS-CANDIDATE-20261018', carrierCode: 'RS', carrierName: '에어서울', cabinAllowanceG: 10_000,
    mainBag: { width: 400, height: 550, depth: 200 },
    includedMainBags: 1, includedPersonalItems: 1, summary: '휴대 가방 10kg 후보 · 실제 운항편 확인 필요',
    exceptionNote: '개인 소지품을 포함한 합산 무게예요. 노선·운항사 안내를 확인하세요.', sourceUrl: 'https://flyairseoul.com/CW/ko/destinations.do', sourceCheckedAt: '2026-10-18' },
  { id: 'ZE-CANDIDATE-20261018', carrierCode: 'ZE', carrierName: '이스타항공', cabinAllowanceG: 10_000,
    includedMainBags: 1, includedPersonalItems: 0, summary: '휴대 가방 10kg 후보 · 실제 운항편 확인 필요',
    exceptionNote: '국내선 영문 운송약관 후보예요. 언어별 치수 안내가 달라 치수는 표시하지 않아요. 국제선·최신 예약 안내를 따로 확인하세요.', sourceUrl: 'https://main.eastarjet.com/pdf/domestic_conditions_of_carriage_en.pdf', sourceCheckedAt: '2026-10-18' },
  { id: 'YP-CANDIDATE-20261018', carrierCode: 'YP', carrierName: '에어프레미아', cabinAllowanceG: 10_000,
    mainBag: { width: 400, height: 550, depth: 200 },
    includedMainBags: 1, includedPersonalItems: 1, summary: '휴대 가방 10kg 후보 · 실제 운항편 확인 필요',
    exceptionNote: '이코노미 기준 후보예요. 프리미엄 좌석·미국 출발편의 개수 예외를 확인하세요.', sourceUrl: 'https://www.airpremia.com/a/en/support/baggage/free-baggage', sourceCheckedAt: '2026-10-18' },
];

export const AIRLINE_SOURCE_POLICY = {
  maxAgeDays: 30,
  // Conservative date: every bundled candidate has been checked by this date.
  checkedAt: AIRLINE_RULE_CANDIDATES.map((candidate) => candidate.sourceCheckedAt).sort()[0],
  securityUrl: 'https://www.avsec365.or.kr/',
};

export function airlineRuleById(id?: string) {
  return AIRLINE_RULE_CANDIDATES.find((candidate) => candidate.id === id);
}

export function matchesCabinDimensions(candidate: AirlineRuleCandidate, dimensionsCm: number[]) {
  if (!candidate.mainBag) return undefined;
  const limits = [candidate.mainBag.height, candidate.mainBag.width, candidate.mainBag.depth];
  return dimensionsCm.length === 3 && dimensionsCm.every((value, index) => Number.isFinite(value) && value > 0 && (candidate.mainBagBoundary === 'exclusive' ? value * 10 < limits[index]! : value * 10 <= limits[index]!));
}
